import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { Notification } from '@shared/models';
import { Pagination } from '@shared/pagination';

import * as NotificationsActions from './notifications.actions';

export interface NotificationsState extends EntityState<Notification> {
  /** true when fetching notifications from server in progress */
  loading: boolean;
  /** server response when there was server connection error in last fetch request for notifications */
  error: any;
  totalUnread: number;
  page: Pagination;
}

export function sortByDate(a: Notification, b: Notification): number {
  return a.createdDate > b.createdDate ? -1 : a.createdDate === b.createdDate ? 0 : 1;
}

export const notificationsAdapter: EntityAdapter<Notification> = createEntityAdapter<Notification>({
  sortComparer: sortByDate,
});

export const initialState: NotificationsState = notificationsAdapter.getInitialState({
  loading: false,
  error: false,
  totalUnread: null,
  page: null,
});

export const userReducer = createReducer(
  initialState,
  on(NotificationsActions.notificationsLoadPage, (state) => ({
    ...state,
    loading: true,
  })),
  on(NotificationsActions.notificationsLoadPageSuccess, (state, { page: { content, ...page } }) =>
    notificationsAdapter.upsertMany(content, {
      ...state,
      loading: false,
      error: null,
      page,
    }),
  ),
  on(NotificationsActions.notificationsLoadPageFail, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(NotificationsActions.notificationsAddNew, (state, { notification }) =>
    notificationsAdapter.setAll(
      [notification, ...Object.values(state.entities).sort((a, b) => b.id - a.id)].slice(0, 10),
      {
        ...state,
        totalUnread: state.totalUnread + 1,
      },
    ),
  ),
  on(NotificationsActions.notificationsUpdateAnonymizedNamesSuccess, (state, { notifications }) =>
    notificationsAdapter.setAll(notifications, {
      ...state,
      loading: false,
    }),
  ),
  on(NotificationsActions.notificationsMarkAsRead, (state) => ({
    ...state,
    totalUnread: 0,
  })),
  on(NotificationsActions.notificationsGetNewNotificationsCountSuccess, (state, { count }) => ({
    ...state,
    totalUnread: count,
  })),
  on(NotificationsActions.notificationsGetNewNotificationsCountFail, (state, { error }) => ({
    ...state,
    error,
  })),
  on(NotificationsActions.notificationsClear, () => ({ ...initialState })),
);

export function notificationsReducer(state: NotificationsState | undefined, action: Action) {
  return userReducer(state, action);
}
